import { useState } from 'react';
import { cn } from '../../lib/cn';
import manifest from '../../assets/images/manifest.json';
import styles from './Picture.module.css';

/**
 * Responsive <picture> driven by the build-time manifest.
 *
 * Each image ships AVIF + WebP at width ladders capped by how large it actually
 * renders (see scripts/figma/fetch-images.mjs). The 24px base64 LQIP is painted
 * as the wrapper's background so there is no layout shift and no extra request.
 */

// CRA cannot build a dynamic import path, so the whole set is required eagerly.
// require.context keeps this to one line instead of 100+ static imports.
const ctx = require.context('../../assets/images', true, /\.(avif|webp)$/);
const byPath = new Map(ctx.keys().map((k) => [k.replace('./', ''), ctx(k)]));

function srcSet(name, ext, widths) {
  return widths
    .map((w) => `${byPath.get(`${name}/${name}-${w}.${ext}`)} ${w}w`)
    .filter((s) => !s.startsWith('undefined'))
    .join(', ');
}

export function Picture({
  name,
  alt = '',
  sizes = '100vw',
  priority = false,
  fit,              // 'contain' for cutouts
  ratio,            // override the intrinsic aspect ratio, e.g. '1 / 1'
  fill = false,     // fill a frame that already defines its own aspect ratio
  zoom = false,     // scale slightly when the card around it is hovered
  className,
  imgClassName,
  style,
  ...rest
}) {
  const [loaded, setLoaded] = useState(false);
  const meta = manifest[name];

  if (!meta) {
    if (process.env.NODE_ENV === 'development') console.warn(`Unknown image: ${name}`);
    return null;
  }

  const widest = meta.widths[meta.widths.length - 1];
  const fallback = byPath.get(`${name}/${name}-${widest}.webp`);

  return (
    <div
      className={cn(
        styles.wrap,
        fill && styles.fill,
        fit === 'contain' && styles.contain,
        zoom && styles.zoom,
        className,
      )}
      // The blurred placeholder only shows behind cover-fit images; a cutout
      // would have the blur visible around its transparent edges.
      style={{
        backgroundImage: fit === 'contain' || loaded ? undefined : `url(${meta.lqip})`,
        // A filling image inherits the frame's ratio; imposing its own here is
        // what makes it overflow the frame.
        aspectRatio: fill ? undefined : (ratio ?? `${meta.width} / ${meta.height}`),
        ...style,
      }}
      {...rest}
    >
      <picture>
        <source type="image/avif" srcSet={srcSet(name, 'avif', meta.widths)} sizes={sizes} />
        <source type="image/webp" srcSet={srcSet(name, 'webp', meta.widths)} sizes={sizes} />
        <img
          src={fallback}
          alt={alt}
          width={meta.width}
          height={meta.height}
          loading={priority ? 'eager' : 'lazy'}
          decoding={priority ? 'sync' : 'async'}
          fetchpriority={priority ? 'high' : undefined}
          onLoad={() => setLoaded(true)}
          className={cn(styles.img, loaded && styles.loaded, imgClassName)}
        />
      </picture>
    </div>
  );
}
